import * as React from 'react';
import { useEffect, useState } from 'react';

import { Dropdown, IDropdownOption, Label, PrimaryButton, Spinner, Stack, TextField, Toggle } from '@fluentui/react';
import { PageHeader } from '../core/PageHeader';
import { HealAPI } from '../../healApi';
import { PATIENT_TYPE } from '../../config/constants';
import { PatientSelector } from '../core/PatientSelector';
import { AccountSelector } from '../core/AccountSelector';
import { MultiSelector } from '../core/MultiSelector';
import {
    deleteUserAccount,
    promotePatient,
    reactivateUserAccount,
    updatePatientUserAccount,
    updateUserAccountUsername
} from '../../api/accounts';
import { InfoMessage } from '../core/InfoMessage';
import { visitSearchByPatientId } from '../../api/elasticsearch';
import { toast } from '../../redux/message/actions';
import { useDispatch } from 'react-redux';

export const AdminTool = () => {
  return (
    <>
      <PageHeader title="Admin Tools" />
      <InfoMessage>These are admin operations - you MUST understand the impact and wait for each operation to complete.</InfoMessage>
      <Stack style={{ marginTop: 20 }} tokens={{ childrenGap: 20 }}>
        <UpdateVisitPatient />
        <UpdatePatientUserAccount />
        <PromotePatient />
        <DeleteUserAccount />
        <ChangeUserAccountUsername />
        <ReactivateUserAccount />
      </Stack>
    </>
  );
};

const UpdateVisitPatient = () => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [sourcePatientId, setSourcePatientId] = useState('');
  const [sourcePatientVisitCodes, setSourcePatientVisitCodes] = useState<string[]>([]);
  const [selectedVisitCodes, setSelectedVisitCodes] = useState<string[]>([]);
  const [destPatientId, setDestPatientId] = useState('');

  useEffect(() => {
    setSelectedVisitCodes([]);
    if (sourcePatientId) {
      visitSearchByPatientId(sourcePatientId).then((results) => {
        const data = results.filter((v) => v.patientId === sourcePatientId);
        setSourcePatientVisitCodes(data.map((v) => v.visitCode));
      });
    }
  }, [sourcePatientId]);

  const canUpdateVisitPatient = (): boolean => {
    return !saving && selectedVisitCodes.length > 0 && sourcePatientId && destPatientId ? true : false;
  };

  const onClick = async () => {
    setSaving(true);
    const errors: string[] = [];
    for (const code of selectedVisitCodes) {
      if (!(await HealAPI.updateVisitPatient(code, destPatientId)).ok) {
        errors.push(code);
      }
    }

    if (errors.length > 0) {
      dispatch(
        toast({
          type: 'error',
          title: 'Error',
          content: 'Unable to move the following visit(s): ' + errors.join(', ') + ', please contact engineering',
        })
      );
    } else {
      dispatch(
        toast({
          type: 'success',
          title: 'Success',
          content: 'Succesfully moved visit(s)',
        })
      );
    }
    setSaving(false);
  };

  return (
    <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
      <Label className="ms-font-xl">Move visit(s)</Label>

      <Stack horizontal={true} tokens={{ childrenGap: 20 }}>
        <PatientSelector readonly={saving} label="Source Patient" onChange={(p) => setSourcePatientId(p.patientId)} />
        <MultiSelector
          label="Select Visits"
          required={true}
          disabled={saving || !sourcePatientId}
          items={sourcePatientVisitCodes.map((i) => ({ key: i, text: i }))}
          selectedKeys={selectedVisitCodes}
          onChange={(v) => setSelectedVisitCodes(v)}
        />
        <PatientSelector readonly={saving} label="Destination Patient" onChange={(p) => setDestPatientId(p.patientId)} />
      </Stack>

      <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
        <PrimaryButton text="Update" disabled={!canUpdateVisitPatient()} onClick={onClick} />
        {saving && <Spinner labelPosition="right" label="Moving visits..." />}
      </Stack>
    </Stack>
  );
};

const UpdatePatientUserAccount = () => {
  const [patientId, setPatientId] = useState('');
  const [userAccountId, setUserAccountId] = useState('');
  const [relationshipId, setRelationshipId] = useState('');
  const [saving, setSaving] = useState(false);

  const canUpdatePatientUserAccount = (): boolean => {
    return !saving && patientId && relationshipId && userAccountId ? true : false;
  };

  const onRelationshipChange = (e: React.FormEvent, item?: IDropdownOption) => {
    if (item) {
      setRelationshipId(item?.key.toString());
    }
  };

  const onClick = () => {
    setSaving(true);
    updatePatientUserAccount(patientId, userAccountId, relationshipId).then(
      () => {
        setRelationshipId('');
        setSaving(false);
      },
      () => setSaving(false)
    );
  };

  return (
    <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
      <Label className="ms-font-xl">Move patient to another user account</Label>

      <Stack horizontal={true} tokens={{ childrenGap: 20 }}>
        <PatientSelector readonly={saving} label="Patient" onChange={(patient) => setPatientId(patient.patientId)} tid="btn_movePatientSourcePatient" />
        <AccountSelector readonly={saving} label="Destination User Account" onChange={(account) => setUserAccountId(account.userAccountId)} tid="ctn_accountSelector" />
        <Dropdown
          onChange={onRelationshipChange}
          options={PATIENT_TYPE.map((m) => ({ key: m.key, text: m.text }))}
          disabled={saving || (!userAccountId && !relationshipId)}
          required={true}
          selectedKey={relationshipId}
          label="New Relationship"
          data-tid="dd_movePatientNewRelationship"
        />
      </Stack>

      <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
        <PrimaryButton text="Update" disabled={!canUpdatePatientUserAccount()} onClick={onClick} data-tid="btn_movePatientUpdate" />
        {saving && <Spinner labelPosition="right" label="Moving patient to another user account..." />}
      </Stack>
    </Stack>
  );
};

const PromotePatient = () => {
  const [patientId, setPatientId] = useState('');
  const [saving, setSaving] = useState(false);

  const canPromotePatient = (): boolean => {
    return !saving && patientId ? true : false;
  };

  const onClick = () => {
    setSaving(true);
    promotePatient(patientId).then(
      () => {
        setPatientId('');
        setSaving(false);
      },
      () => setSaving(false)
    );
  };

  return (
    <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
      <Label className="ms-font-xl">Promote patient into new user account</Label>

      <Stack horizontal={true} tokens={{ childrenGap: 20 }}>
        <PatientSelector readonly={saving} label="Patient" onChange={(p) => setPatientId(p.patientId)} />
      </Stack>

      <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
        <PrimaryButton text="Promote" disabled={!canPromotePatient()} onClick={onClick} />
        {saving && <Spinner labelPosition="right" label="Promoting patient into new user account..." />}
      </Stack>
    </Stack>
  );
};

const DeleteUserAccount = () => {
  const [userAccountId, setUserAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);
  const [actionText, setActionText] = useState('Disable');

  const onClick = () => {
    setSaving(true);
    deleteUserAccount(userAccountId, hardDelete).then((ok) => {
      if (ok) {
        setUserAccountId('');
      }
      setSaving(false);
    });
  };

  const onToggleChange = (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    setHardDelete(checked!);
    setActionText(checked! ? 'Delete' : 'Disable');
  };

  return (
    <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
      <Label className="ms-font-xl">Disable or Delete a user account</Label>
      <Toggle disabled={saving} defaultChecked={false} label="Disable or Delete" onText="Delete" offText="Disable" onChange={onToggleChange} />
      <AccountSelector
        readonly={saving}
        label="User Account"
        userAccountId={userAccountId}
        onChange={(account) => setUserAccountId(account.userAccountId)}
        tid="ctn_disableOrDeleteUserAccount"
      />
      <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
        <PrimaryButton text={actionText} disabled={saving || !userAccountId} onClick={onClick} />
        {saving && <Spinner labelPosition="right" label={`${actionText === 'Delete' ? 'Deleting' : 'Disabling'} user account...`} />}
      </Stack>
    </Stack>
  );
};

const ChangeUserAccountUsername = () => {
  const [userAccountId, setUserAccountId] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  const [saving, setSaving] = useState(false);

  const onClick = () => {
    setSaving(true);
    updateUserAccountUsername(userAccountId, newUsername).then((ok) => {
      if (ok) {
        setUserAccountId('');
        setCurrentUsername('');
        setNewUsername('');
      }
      setSaving(false);
    });
  };

  return (
    <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
      <Label className="ms-font-xl">Change a user account username</Label>
      <Stack horizontal={true} tokens={{ childrenGap: 20 }}>
        <AccountSelector
          readonly={saving}
          label="User Account"
          userAccountId={userAccountId}
          onChange={(account) => {
            setUserAccountId(account.userAccountId);
            setCurrentUsername(account.email);
          }}
          tid="ctn_changeUserNameUserAccount"
        />
        <TextField style={{ width: 300 }} label="Current Username" value={currentUsername} readOnly={true} data-tid="inp_changeUsernameOldName" />
        <TextField
          style={{ width: 300 }}
          label="New Username"
          value={newUsername}
          readOnly={saving}
          data-tid="inp_changeUsernameNewName"
          onChange={(e, v) => setNewUsername(v || '')}
          required={true}
        />
      </Stack>
      <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
        <PrimaryButton text="Update" disabled={saving || (!userAccountId && !newUsername)} onClick={onClick} data-tid="btn_update" />
        {saving && <Spinner labelPosition="right" label="Updating user account..." />}
      </Stack>
    </Stack>
  );
};

const ReactivateUserAccount = () => {
    const [userAccountId, setUserAccountId] = useState('');
    const [saving, setSaving] = useState(false);

    const canReactivateUserAccount = (): boolean => {
        return !!(!saving && userAccountId);
    };

    const onClick = () => {
        setSaving(true);
        reactivateUserAccount(userAccountId).then((ok) => {
            if (ok) {
                setUserAccountId('');
            }
            setSaving(false);
        });
    };

    return (
        <Stack className="ms-depth-4" tokens={{ childrenGap: 10 }} style={{ padding: 20 }}>
            <Label className="ms-font-xl">Reactivate user account</Label>

            <Stack horizontal={true} tokens={{ childrenGap: 20 }}>
                <AccountSelector
                    readonly={saving}
                    label="User Account"
                    userAccountId={userAccountId}
                    onChange={(account) => setUserAccountId(account.userAccountId)}
                    tid="ctn_reactivateUserAccount"
                />
            </Stack>

            <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="center">
                <PrimaryButton text="Reactivate" disabled={!canReactivateUserAccount()} onClick={onClick} />
                {saving && <Spinner labelPosition="right" label="Reactivating user account..." />}
            </Stack>
        </Stack>
    );
};